<script setup>
  import { computed, onMounted, nextTick, inject, ref } from "vue";
  import { storeToRefs } from "pinia";
  import { Badge } from "@/components/ui/badge";
  import { isCompactCard, isFoldedByDefault, isLogicCard } from "@utils/composables/reviewDocument";
  import { getCardGroupColors } from "@utils/constants/cardTypes";
  import { Button } from "@/components/ui/button";
  import { Tooltip } from "@/components/ui/tooltip";
  import { ChevronRight, Pencil, MessageSquare } from "lucide-vue-next";
  import ReviewCardBody from "./ReviewCardBody.vue";
  import ReviewCardOptions from "./ReviewCardOptions.vue";
  import ReviewConditionLine from "./ReviewConditionLine.vue";
  import { useQuestionCardTypes, useCardAttributes } from "@utils/composables/questionCard";
  import { useReviewCard } from "@utils/composables/reviewCard";
  import { useCurrentCardStore } from "@stores/survey/currentCard/currentCard";
  import { useSurveyCardsStore } from "@stores/survey/surveyCards";
  import { useReviewCommentsStore } from "@stores/survey/reviewComments";
  import { useSurveyStore } from "@stores/survey/survey";
  import { isEmpty, renderToHtml } from "@utils/toolBox";

  const currentCardStore = useCurrentCardStore();
  const { currentToken } = currentCardStore;

  const { canWriteSurvey } = storeToRefs(useSurveyStore());

  const surveyCardsStore = useSurveyCardsStore();
  const { selectCurrentCard, redirectToEdit } = surveyCardsStore;
  const { firstLoadReviewPage } = storeToRefs(surveyCardsStore);

  const reviewStore = useReviewCommentsStore();

  const props = defineProps({
    card: { type: Object, required: true },
    cardSubTypesInfos: { type: Object, required: true },
    cardTypesGroups: { type: Array, required: true },
    showOptions: { type: Boolean, default: true },
    showAnswers: { type: Boolean, default: true },
    showConditions: { type: Boolean, default: true },
  });

  const compact = computed(() => isCompactCard(props.card));
  const logic = computed(() => isLogicCard(props.card));
  const commentCount = computed(() => reviewStore.commentCountForCard(props.card.token));

  const foldable = computed(() => isFoldedByDefault(props.card));
  const open = ref(false);
  const showsBody = computed(() => !compact.value && (!foldable.value || open.value));

  const cardAppears = computed(() => {
    return !isEmpty(document.getElementById(`review-card-${currentToken}`));
  });

  onMounted(async () => {
    await nextTick(() => {
      if (cardAppears.value && firstLoadReviewPage.value)
        reviewEventBus.emit({ command: "scroll", token: currentToken });
    });
  });

  const { type, name, cardsTitle, hint, hasHint, isRandomized, token } = useCardAttributes();
  const { determineSubTypeLabel, determineIcon } = useQuestionCardTypes(props.cardSubTypesInfos);
  const { reviewCardFields, reviewCardOptions } = useReviewCard(props.card);
  const questionEventBus = inject("questionEventBus");
  const reviewEventBus = inject("reviewEventBus");
  const editCard = (cardToken) => {
    selectCurrentCard({ token: cardToken });
    redirectToEdit({ redirect: true, token: cardToken });
    questionEventBus.emit({ command: "changeTab", token: cardToken });
  };
</script>

<template>
  <div
    :id="`review-card-${card.token}`"
    class="group relative -mx-4 rounded-lg px-4 transition-colors prose-a:text-primary prose-a:underline prose-a:underline-offset-2"
    :class="[
      logic ? 'my-2 bg-muted py-4' : compact ? 'py-3 hover:bg-muted/50' : 'py-6 hover:bg-muted/50',
      card.is_for_analytics && 'opacity-50',
    ]"
    data-cy="review-card">
    <div class="flex flex-wrap items-center gap-2.5 pr-18">
      <button
        v-if="foldable"
        type="button"
        class="-ml-1 flex size-5 shrink-0 items-center justify-center rounded text-foreground-subtle hover:text-primary"
        :aria-expanded="open"
        :aria-label="open ? 'Hide details' : 'Show details'"
        @click="open = !open">
        <ChevronRight
          class="size-4 transition-transform"
          :class="open && 'rotate-90'" />
      </button>
      <Tooltip :content="determineSubTypeLabel(type(card))">
        <span
          class="flex size-6 shrink-0 cursor-help items-center justify-center rounded-md"
          :class="[getCardGroupColors(card).bg, getCardGroupColors(card).text]">
          <component
            :is="determineIcon(type(card))"
            class="size-3.5"
            :stroke-width="1.8" />
        </span>
      </Tooltip>
      <span class="text-13 font-medium text-primary">{{ name(card) || "Untitled" }}</span>
      <ReviewCardOptions
        v-if="compact && reviewCardOptions.length"
        inline
        :card="card"
        :options="reviewCardOptions" />
      <Badge
        v-if="isRandomized(card)"
        variant="neutral">
        Randomized
      </Badge>
    </div>

    <ReviewConditionLine
      v-if="showConditions !== false && !isEmpty(card.specs?.conditions)"
      class="mt-2"
      :conditions="card.specs.conditions" />

    <div
      class="absolute right-3 flex items-center gap-1"
      :class="logic ? 'top-2.5' : compact ? 'top-1.5' : 'top-4.5'">
      <Tooltip content="Comments on this card">
        <Button
          variant="ghost"
          size="icon-sm"
          class="text-muted-foreground hover:text-primary"
          data-comment-trigger
          @click="reviewStore.openCardComments(token(card))">
          <MessageSquare class="size-4" />
        </Button>
      </Tooltip>
      <Tooltip
        v-if="canWriteSurvey"
        content="Edit">
        <Button
          variant="ghost"
          size="icon-sm"
          class="text-muted-foreground hover:text-primary"
          @click="editCard(token(card))">
          <Pencil class="size-4" />
        </Button>
      </Tooltip>
    </div>

    <button
      v-if="commentCount > 0"
      type="button"
      class="absolute -right-11 top-5 flex items-center gap-1 text-xs font-semibold text-primary"
      data-comment-trigger
      @click="reviewStore.openCardComments(token(card))">
      <MessageSquare class="size-3.5" />
      {{ commentCount }}
    </button>

    <template v-if="showsBody">
      <!-- eslint-disable vue/no-v-html -->
      <p
        v-if="!isEmpty(cardsTitle(card))"
        class="mt-2.5 text-base font-medium leading-normal text-foreground"
        v-html="$sanitize(renderToHtml(cardsTitle(card)))"></p>
      <span
        v-if="hasHint(card)"
        class="mt-1 block text-13 italic text-muted-foreground"
        v-html="$sanitize(renderToHtml(hint(card)))"></span>
      <!--eslint-enable-->

      <ReviewCardOptions
        v-if="showOptions !== false && !isEmpty(reviewCardOptions)"
        class="mt-1.5"
        :card="card"
        :options="reviewCardOptions" />

      <div
        v-if="showAnswers !== false"
        class="mt-2">
        <ReviewCardBody
          :card="card"
          :fields="reviewCardFields" />
      </div>
    </template>
  </div>
</template>
